<template>
  <lf-modal
    :title="
      signedDocOnly
        ? $t('DEALS.DOCUMENTS.ASSIGN_APPLICATION_TO_OWNER')
        : $t('DEALS.DOCUMENTS.ADD_NEW')
    "
    :close="handleModalClose"
  >
    <template #content>
      <div>
        <div
          v-if="showDocDropZone"
          class="m-6 flex h-80 max-w-full flex-col items-center justify-center border border-dashed border-gray-300 bg-gray-50"
          @drop.prevent="handleDrop"
          @dragenter.prevent
          @dragover.prevent
        >
          <div
            class="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100"
          >
            <icon-base width="30" height="30" :icon="IconUploadFile" />
          </div>
          <span class="mt-6 w-60 text-center text-sm text-gray-500">
            {{
              $t("DEALS.DOCUMENTS.UPLOAD_TEXT_3", {
                docTypes: "doc, pdf, xls, jpg, gif, png, heic",
              })
            }}
            <input
              id="browse"
              type="file"
              multiple
              class="hidden"
              accept=".doc, .pdf, .xls, .xlsx, image/*, .docx, .heic"
              @change="handleFileChange"
            />
            <label
              for="browse"
              class="cursor-pointer text-primary"
              data-cy="upload-document-input"
            >
              {{ $t("DEALS.DOCUMENTS.UPLOAD_TEXT_BROWSE") }}
            </label>
            {{ $t("DEALS.DOCUMENTS.UPLOAD_TEXT_2") }}
          </span>
        </div>
        <div v-if="filesQueue?.length">
          <div>
            <document-upload-item
              v-for="(file, index) in filesQueue"
              :key="file.id"
              :file="file"
              :owner-id="ownerId"
              :owners="owners"
              :signed-doc-only="signedDocOnly"
              :upload-triggered="uploadLoading"
              :show-loading-spinner="uploadLoading"
              :style="`z-index: ${
                filesQueue?.length ? filesQueue.length - index : 1
              }`"
              @product-type-selected="handleDocumentProductTypeChange"
              @owner-selected="handleDocumentOwnerChange"
              @option-selected="handleFileTypeChange"
              @file-cancelled="removeFileFromQueue"
            />
          </div>
        </div>
        <div
          v-if="!uploadLoading"
          class="flex min-w-full items-center justify-end space-x-2 border-t py-6 pr-5"
        >
          <outline-button @click.prevent="handleModalClose">
            {{ $t("COMMON.CANCEL") }}
          </outline-button>
          <primary-button
            data-cy="upload-document-confirm"
            :disabled="isUploadDisabled"
            @click="handleUploadClick"
          >
            {{ $t("DEALS.DOCUMENTS.UPLOAD") }}
          </primary-button>
        </div>
      </div>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { getIndividualName } from "@/helpers/common";
import { SIGNED_DOC_FILE_TYPE } from "@/helpers/constants";
import { areFilesExtensionsValid, getExtensionFromName } from "@/helpers/files";
import { makeRandomString } from "@/helpers/formatting";
import { usePromiseWrapper } from "@/hooks/common";
import { useDealOwners, useDealsBase } from "@/hooks/deals";
import { useNotification } from "@/hooks/notifications";
import applicationsApiService from "@/services/modules/applications";

import DocumentUploadItem from "@/components/deals/documents/DocumentUploadItem.vue";
import IconUploadFile from "@/components/icons/deals/IconUploadFile.vue";
import LfModal from "@/components/ui/Modal.vue";

import type { FileWBlob, IFile, IFilePayload } from "@/models/common";
import type { PropType } from "vue";

const IMAGE_TYPES = ["gif", "jpeg", "jpg", "png", "heic"];
const ALLOWED_FILES_TYPES = [
  ...IMAGE_TYPES,
  "xls",
  "pdf",
  "doc",
  "docx",
  "xlsx",
];

const props = defineProps({
  ownerId: {
    type: String,
  },
  signedDocOnly: {
    type: Boolean,
    default: false,
  },
  close: {
    type: Function,
    required: true,
  },
  filesToUpload: {
    type: Object as PropType<File[] | null>,
  },
  showDocDropZone: {
    type: Boolean,
    default: true,
  },
  filesType: {
    type: String,
    default: "",
  },
  closeIfNoDocs: {
    type: Boolean,
    default: false,
  },
});

const { commit } = useStore();
const { t } = useI18n();
const { showMessage } = useNotification();
const allOwners = useDealOwners();
const { activeDeal } = useDealsBase();

const filesQueue = ref<FileWBlob[]>([]);
const uploadedFiles = ref<IFile[]>([]);

const { loading: uploadLoading, fetchWrapper: uploadQueuedDocuments } =
  usePromiseWrapper(
    async () => {
      for (const file of filesQueue.value) {
        file.failed = false;
      }

      const filesPayload: IFilePayload[] = filesQueue.value.map((file) => {
        if (props.signedDocOnly) {
          file.documentType = SIGNED_DOC_FILE_TYPE;
        }

        const filePayload: IFilePayload = {
          file: file.blob,
        };

        if (file.documentType === SIGNED_DOC_FILE_TYPE) {
          filePayload.owner_id =
            props.ownerId || Object.keys(owners.value)?.[0];
        }

        if (file.ownerId) {
          filePayload.owner_id = file.ownerId;
        }

        if (file.productTypeId) {
          filePayload.product_id = file.productTypeId;
        }

        return filePayload;
      });

      const uploadedFilesResult =
        await applicationsApiService.uploadMultipleApplicationFiles(
          activeDeal.value.id,
          filesPayload
        );

      // This endpoint only succeeds if all files upload successfully, so we can
      // mark all files as uploaded
      for (const file of filesQueue.value) {
        file.uploaded = true;
      }

      uploadedFiles.value.push(...uploadedFilesResult);
    },
    {
      onError: () => {
        // Similarly, mark all files as failed
        for (const file of filesQueue.value) {
          file.failed = true;
        }
      },
    }
  );

const isUploadDisabled = computed(
  () =>
    (filesQueue.value.length < 1 || uploadLoading.value) && !props.signedDocOnly
);

const owners = computed(() => {
  return allOwners.value.reduce<Record<string, string>>((acc, curr, index) => {
    acc[curr.id] = getIndividualName(curr, index);
    return acc;
  }, {});
});

const handleModalClose = () => {
  if (uploadedFiles.value?.length) {
    commit("applications/addFilesToActiveApp", uploadedFiles.value);
  }
  filesQueue.value = [];
  uploadedFiles.value = [];
  props.close();
};

const getIndexOfFileInQueue = (id: string | undefined) => {
  if (!id) {
    return -1;
  }

  return filesQueue.value.findIndex((file) => file.id === id);
};

const handleFileChange = (e: Event) => {
  enqueueFiles((e?.target as HTMLInputElement)?.files);
  // clear the input value because otherwise
  // onChange won't work next time you select the same file
  (e.target as HTMLInputElement).value = "";
};

const handleFileTypeChange = (id: string, documentType: string) => {
  const index = getIndexOfFileInQueue(id);
  if (index !== -1) {
    filesQueue.value[index].documentType = documentType;
  }
};

const handleDocumentProductTypeChange = (id: string, option: string) => {
  const index = getIndexOfFileInQueue(id);
  if (index === -1) {
    return;
  }
  filesQueue.value[index].productTypeId = option;
};

const handleDocumentOwnerChange = (id: string, option: string) => {
  const index = getIndexOfFileInQueue(id);
  if (index === -1) {
    return;
  }
  filesQueue.value[index].ownerId = option;
};

const removeFileFromQueue = (id: string) => {
  const fileIndex = getIndexOfFileInQueue(id);

  if (fileIndex === -1) {
    return;
  }
  return filesQueue.value.splice(fileIndex, 1);
};

const handleDrop = (dropEvent: DragEvent) => {
  if (dropEvent?.dataTransfer) {
    const fileArray = Object.values(dropEvent.dataTransfer.files);
    if (areFilesExtensionsValid(fileArray, ALLOWED_FILES_TYPES)) {
      enqueueFiles(dropEvent.dataTransfer.files);
    }
  }
};

const getFilesWithValidSize = (files: FileList | File[]) => {
  const filesArray = Object.values(files);
  const filesWithValidSize = filesArray.filter(
    (file) => Math.round(file.size / 1024) < 100_000
  );
  if (filesArray?.length !== filesWithValidSize?.length) {
    showMessage(
      t("DEALS.DOCUMENTS.FILE_BIG"),
      "error",
      t("DEALS.DOCUMENTS.FILE_BIG_SUBTITLE")
    );
  }
  return filesWithValidSize;
};

const enqueueFiles = (files: FileList | File[] | null | undefined) => {
  if (!files) {
    return;
  }
  const toUpload = getFilesWithValidSize(files);
  filesQueue.value = [
    ...filesQueue.value,
    ...toUpload.map((file) => {
      const type = getExtensionFromName(file.name);
      return {
        id: makeRandomString(),
        name: file.name,
        progress: 0,
        failed: false,
        uploaded: false,
        size: Math.round(file.size / 1024), // in kbytes
        type,
        documentType: null,
        imagePreviewUrl: IMAGE_TYPES.includes(type)
          ? URL.createObjectURL(file)
          : null,
        blob: file,
      };
    }),
  ];
};

const handleUploadClick = async () => {
  if (!filesQueue.value?.length) {
    return;
  }

  await uploadQueuedDocuments();

  if (!filesQueue.value.some((file) => file.failed)) {
    handleModalClose();
  }
};

watch(
  filesQueue,
  (queue) => {
    if (!queue.length && props.closeIfNoDocs) {
      props.close();
    }
  },
  { deep: true }
);

onMounted(() => enqueueFiles(props.filesToUpload));
</script>
